import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'

import { IFilter, Logic, Operator } from 'core-components/types'
import { getContactsByEmail } from 'partner360/features/hpContacts/api/getContactsByEmail'
import { getJbpHpContactsCrud } from 'partner360/features/hpContacts/lib'

interface IUseHPContactsParameters {
  partnerId: string
  crudKey?: string
  roles?: string
  shouldFetchAllRoles?: boolean
}

export const useHpContacts = ({
  crudKey = 'JBPSalesTeam-WithTickets',
  roles = '',
  partnerId,
  shouldFetchAllRoles,
}: IUseHPContactsParameters) => {
  /** -------------------- Variables ----------------------- */
  const [tableFetch, setTableFetch] = useState(-1)
  const jbpHpContactChangeRequestCrud = getJbpHpContactsCrud(crudKey)

  const partnerIdFilter = useMemo<IFilter>(
    () => ({
      field: 'PartnerId',
      operator: Operator.EQUAL,
      value: partnerId,
    }),
    [partnerId]
  )

  const rolesFilter = useMemo<IFilter | null>(() => {
    if (shouldFetchAllRoles) {
      return null
    }

    return {
      field: 'Roles',
      operator: Operator.CONTAINS,
      value: roles,
    }
  }, [shouldFetchAllRoles, roles])

  const filters = useMemo(
    () => [partnerIdFilter, rolesFilter].filter((it): it is IFilter => it !== null),
    [partnerIdFilter, rolesFilter]
  )

  /** -------------------- Methods ----------------------- */
  const handleGetContactsByEmail = useCallback(
    async (email: string) => {
      return await getContactsByEmail({
        partnerId,
        email,
        isForce: true,
      })
    },
    [partnerId]
  )

  const refreshTable = useCallback(() => {
    setTableFetch(1)
  }, [])

  const resetCrudStorage = useCallback(() => {
    setTableFetch(-1)
    jbpHpContactChangeRequestCrud.removeQueries(true)
  }, [jbpHpContactChangeRequestCrud])

  /** -------------------- Effects ----------------------- */
  useEffect(() => {
    jbpHpContactChangeRequestCrud.setPaging({
      page: 0,
      pageSize: 5,
    })

    jbpHpContactChangeRequestCrud.setSort({
      field: 'Modified',
      dir: 'desc',
    })
  }, [jbpHpContactChangeRequestCrud])

  useLayoutEffect(() => {
    if (roles) {
      jbpHpContactChangeRequestCrud.setFilter({
        logic: Logic.AND,
        filters,
      })

      setTableFetch(1)
    }

    return () => {
      setTableFetch(-1)
    }
  }, [roles, jbpHpContactChangeRequestCrud, filters])

  return {
    jbpHpContactChangeRequestCrud,
    tableFetch,
    getContactsByEmail: handleGetContactsByEmail,
    resetCrudStorage,
    refreshTable,
  }
}
